import React, { useEffect, useState } from 'react';
import { msalConfig } from 'client/utils/auth/authConfig';
import { getAccessToken, loginRedirect, logoutRedirect } from 'client/utils/auth/msalHelpers';
import { ProfileService } from 'client/services';
import { useIdleTimer } from 'react-idle-timer';
import { useIsAuthenticated } from '@azure/msal-react';
import AuthContext from 'client/utils/auth/AuthContext/AuthContext';
import { logWithPrefix } from 'client/utils/logging';
import store from 'client/store/store';
import { loadMapConfig } from 'client/store/mapConfigSlice/mapConfigSlice';
import { portalBaseUrl } from 'client/utils/constants/frontEndConfig';
import { SERVICE_UNAVAILABLE_PAGE, USER_PROFILE_NOT_FOUND_PAGE } from 'client/utils/routes/routeConstants';
import { setLocale } from 'client/store/localeSlice/localeSlice';
import { LOCALES } from 'i18n/locales';

const log = logWithPrefix('AuthProvider');

const minute = 60 * 1000;

// Avoiding using visibilitychange as this stops it from working when machine sleeps
const events = [
    'mousemove',
    'keydown',
    'wheel',
    'DOMMouseScroll',
    'mousewheel',
    'mousedown',
    'touchstart',
    'touchmove',
    'MSPointerDown',
    'MSPointerMove',
    'blur'
];

/**
 * Provides the user & permissions.
 * Turns on the prompt to continue the session after a period of inactivity
 * Automatically signs the user out if their session time is hit
 *
 * Current behaviour:
 * - If a user moves their mouse, the idle timer is reset
 * - If a user moves their mouse while the prompt to continue the session is up, the idle timer keeps counting down
 * - If a user clicks on OK to continue session, the access token may have expired but can be refreshed
 */
const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [prompting, setPrompting] = useState(false);
    const [loading, setLoading] = useState(false);
    const [permissions, setPermissions] = useState([]);
    const isAuthenticated = useIsAuthenticated();

    /**
     * The user profile is required to access many features of the application.
     * If a user profile is not found, force a sign-out and redirect to a no-auth required page.
     */

    const loadUser = () => {
        setLoading(true);
        ProfileService.getUserProfile().then(user => {
            // As soon as we have a user, load their map config so we can start making epsg-enabled API calls.
            store.dispatch(loadMapConfig(user.mapConfiguration));
            // store the default locale so this is available to render
            const currentLocale = user?.preferredLanguage;
            if (currentLocale && (currentLocale === LOCALES.ENGLISH || currentLocale === LOCALES.WELSH)) {
                store.dispatch(setLocale(currentLocale));
            }

            const message = `Got profile for user ${user.subscriberId} with organisation ${user.organisationId} having preference locale of ${currentLocale} successfully.`;
            log('Got User Profile', message);
            setUser(user);
            setLoading(false);
        }).catch((error) => {
            if (error.status === 404 || error.code === 404) {
                log('User profile not found', 'redirecting to user not found error page').promise.finally(() => {
                    signOutRedirect(`${portalBaseUrl}${USER_PROFILE_NOT_FOUND_PAGE}`);
                });
            } else {
                log('Profile service failed - signing user out').promise.finally(() => {
                    signOutRedirect(`${portalBaseUrl}${SERVICE_UNAVAILABLE_PAGE}`);
                });
            }
        });
    };

    const signIn = () => {
        loginRedirect();
    };

    const signOutByChoice = () => {
        setLoading(true);
        log('Deliberate sign out').promise.finally(() => signOutRedirect());
    };

    const signOutRedirect = (postLogoutRedirectUri = msalConfig.auth.postLogoutRedirectUri) => {
        setLoading(true);
        setUser(null);
        logoutRedirect(postLogoutRedirectUri);
    };

    const refresh = () => {
        if (isAuthenticated) {
            getAccessToken(); // Keeps refresh token alive
            reset();
        }
        setPrompting(false);
    };

    const _onIdle = () => {
        if (isAuthenticated) {
            signOutRedirect();
        }
    };

    const _onAction = () => {
        if (isAuthenticated && !isPrompted() && getRemainingTime() > 0) {
            reset();
        }
    };

    const _onPrompt = () => {
        if (isAuthenticated) {
            setPrompting(true); // Opens prompt through useEffect hook on AutoLogout
        }
    };

    const { reset, isPrompted, getRemainingTime } = useIdleTimer({
        onPrompt: _onPrompt, // Function to display prompt when prompt fires
        onIdle: _onIdle, // Function to call sign out when timeout fires
        onAction: _onAction, // Function to reset timeout when action occurs on page
        events,
        timeout: 60 * minute, // The total time of being idle before logging out
        promptBeforeIdle: 1 * minute, // How long before automatic logout to show to prompt
        eventsThrottle: 500 // Calls onaction every half a second, as the last minute is the prompt dont need to be too reactive
    });

    useEffect(() => {
        if (isAuthenticated) {
            log('isAuthenticated', true);
            loadUser();
        }
    }, [isAuthenticated]);

    useEffect(() => {
        if (user) {
            setPrompting(false);
            if (user.roles) {
                const permissions = new Set(user?.roles?.flatMap(r => r.permissions).map(permission => permission.type));
                setPermissions(Array.from(permissions));
            }
        }
    }, [user]);

    return (
        <AuthContext.Provider value={{ user, permissions, signIn, signOutByChoice, signOutRedirect, prompting, loading, refresh, isAuthenticated, loadUser }}>
            {children}
        </AuthContext.Provider>
    );
};

export default AuthProvider;
